import { useState } from "react";
import { LightningCharge } from "react-bootstrap-icons";

import { GasMeterDetailsViewProps } from "../../types/components.types";
import { downloadGasMeterDetailsCSV } from "../../utils/gaslink/gaslink.csv";
import Button from "../layout/Button";

// Declare a functional component using <MeterDetailsViewProps> for type safety.
// Pass meterDetails as a prop. meterDetails contains all the relevant details for a specific meter to display the details on the UI and export it as CSV.
const GasMeterDetailsView: React.FC<GasMeterDetailsViewProps> = ({ gasMeterDetails }) => {
  const [showMeterDetails, setShowMeterDetails] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);

  const displayMeterDetails = () => {
    setShowMeterDetails(!showMeterDetails);
  };

  const handleDownloadCSV = async () => {
    setIsDownloading(true);

    try {
      const result = await downloadGasMeterDetailsCSV(gasMeterDetails);

      if (!result.success) {
        console.log("Failed to download CSV:", result.error);
        alert(`Failed to download CSV: ${result.error ?? "Unknown error"}. Please try again.`);
      }
    } catch (error) {
      console.log("Error during CSV download:", error);
      alert("An error occurred while downloading the CSV. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  // Organise meter details into categories for better display
  const organiseDataForDisplay = () => {
    const completeData = gasMeterDetails;

    // Extract the nested objects and properties for an organised display.
    const { ...mainData } = completeData;

    return {
      mainDetails: mainData,
    };
  };

  const { mainDetails } = organiseDataForDisplay();

  const fieldNameMappings: Record<string, string> = {
    houseName: "House Name",
    subBuildingName: "Sub Building Name",
    houseNumber: "House Number",
    dependentStreet: "Dependent Street",
    street: "Street",
    postTown: "Town",
    postcode: "Postcode",
    county: "County",
    currentSupplierId: "Current Supplier ID",
    currentSupplierName: "Current Supplier Name",
    current_supplier_name: "Current Supplier Name",
    currentSupplierRegEffectiveDate: "Current Supplier Reg Effective Date",
    pendingSupplierRegEffectiveDate: "Pending Supplier Reg Effective Date",
    mprn: "MPRN",
    meterSerialNumber: "Meter Serial Number",
    mpaq: "MPAQ",
    formulaYearSupplyMeterPointAq: "Formula Year Supply Meter Point AQ",
    addressId: "Address ID",
    gasTransportId: "Gas Transport ID",
    ldzId: "LDZ ID",
    meterCapacity: "Meter Capacity",
    meterMechanismCode: "Meter Mechanism Code",
    ndmq: "NDMQ",
    poBoxNumber: "PO Box Number",
    smartEquipmentTechnicalCode: "Smart Equipment Technical Code",
    deliveryPointAlias: "Delivery Point Alias",
    doubleDependentLocality: "Double Dependent Locality",
    lastMeterReadDate: "Last Meter Read Date",
    lastMeterReadType: "Last Meter Read Type",
    last_meter_read_value: "Last Meter Read Value",
    market_sector_code: "Market Sector Code",
    meter_device_status: "Meter Device Status",
    meter_imperial_indicator: "Meter Imperial Indicator",
    meter_installation_date: "Meter Installation Date",
    meter_location: "Meter Location",
    meter_manufacturer: "Meter Manufacturer",
    meter_model: "Meter Model",
    meter_number_of_dials: "Meter Number Of Dials",
    meter_point_status: "Meter Point Status",
    meter_type: "Meter Type",
    meter_units: "Meter Units",
    meter_year_of_manufacture: "Meter Year Of Manufacture",
    network_name: "Network Name",
    previous_supplier_name: "Previous Supplier Name",
    previous_supplier_short_code: "Previous Supplier Short Code",
    addressSource: "Address Source",
    uprn: "UPRN",
    country: "Country",
    dmq: "DMQ",
    amrIndicator: "AMR Indicator",
    convertorIndicator: "Convertor Indicator",
    correctionFactor: "Correction Factor",
    exitZone: "Exit Zone",
    icuktsflg: "ICUKTS Flag",
    incomingSupplier: "Incoming Supplier",
    installingSupplierId: "Installing Supplier ID",
  };

  return (
    <div className="meter-details">
      {showMeterDetails ? (
        <>
          <div className="meter-details-header">
            <h2>
              <LightningCharge className="me-2" />
              Gas Meter Details
            </h2>
            <Button onClick={handleDownloadCSV} disabled={isDownloading}>
              Download CSV
            </Button>
          </div>

          <div className="row g-3">
            <table
              className="table table-bordered"
              style={{ width: "90%", margin: "0 auto", marginTop: "15px" }}
            >
              <tbody>
                {Object.entries(mainDetails)
                  // Filter out empty/null/undefined values, arrays, and nested objects to not render on the UI.
                  .filter(
                    (entry): entry is [string, string | number] =>
                      entry[0] !== "status_code" &&
                      entry[0] !== "relAddress" &&
                      entry[1] !== "" &&
                      typeof entry[1] !== "object",
                  )
                  .map(([key, value]) => (
                    <tr key={key}>
                      <th style={{ width: "20%" }}>
                        {key in fieldNameMappings
                          ? fieldNameMappings[key]
                          : key.replace(/_/g, " ").toUpperCase()}
                      </th>
                      <td>{String(value)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
      <div className="meter-details-footer">
        <Button onClick={displayMeterDetails}>
          {showMeterDetails ? "Hide Details" : "Show Meter Details"}
        </Button>
      </div>
    </div>
  );
};

export default GasMeterDetailsView;
